import { useEffect, useState } from "react";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";

import { createTask } from "../api/tasks";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

// ClientCreateTaskModal — feature-local T-3 modal asset.
//
// Closed-class reference: architecture-freeze-r8.md §4 T-3
// ("section owns slice + modal; parent header has a presentation-alias
// trigger that calls a one-method ref bridge"). Bridge constraints
// per I2 §"R7 §6.4" proposal hold on the section, not on this file —
// this widget never sees the parent. It receives `open` / `onClose` /
// `clientId` / `onSaved` from its single owner ([[ClientTasksSection]])
// and knows nothing else.
//
// Ownership:
//   * open/close state — owned by [[ClientTasksSection]] via plain
//     useState; this component is fully controlled.
//   * mutation — owned here (POST /tasks/ via the feature-local API
//     wrapper). The section is NOT involved in the mutation.
//   * post-success freshness — owned by the section: this widget
//     calls onSaved() once the POST resolves; the section closes the
//     modal and refetches its own slice. No parent involvement, no
//     cross-section refresh, no orchestration.
//
// Reuses the intra-feature `.feature-client-detail__modal*` CSS rules
// introduced for the T-1 ClientEditModal — same feature scope, NOT a
// shared modal primitive. No portal, no provider, no registry, no
// shared overlay host. Sibling of [[ClientEditModal]],
// [[ClientContactsEditModal]], and [[ClientLogCommunicationModal]].
//
// Form fields kept deliberately minimal: title (required) /
// description / priority (default medium) / due_date (optional). The
// design HTML's task modal carries exactly these four; status is
// not asked because backend defaults to "todo", and assigned_to is
// not asked because backend defaults to the requesting user.
// Anything beyond these four would be R7 §12.5 forbidden invention.
//
// Effects in this primitive are limited to the allowed UI-local
// effects per CLAUDE.md "Presentation Primitive Rules": ESC-key
// handler, body scroll lock, and a draft-reset on (re)open. No
// selectors, no dispatch, no imperative handle, no registry, no
// portal, no shared modal host.

const PRIORITY_OPTIONS = [
  { value: "low", label: "Низький" },
  { value: "medium", label: "Середній" },
  { value: "high", label: "Високий" },
];

const EDITABLE_KEYS = ["title", "description", "priority", "due_date"];

const INITIAL_DRAFT = {
  title: "",
  description: "",
  priority: "medium",
  due_date: "",
};

// Convert <input type="datetime-local"> output ("YYYY-MM-DDTHH:MM")
// into an ISO string the DRF DateTimeField accepts. Returns null
// when the user left the field empty.
const dueDateForApi = (value) => {
  if (typeof value !== "string" || value.trim().length === 0) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString();
};

function ClientCreateTaskModal({ open, onClose, clientId, onSaved }) {
  const [draft, setDraft] = useState(INITIAL_DRAFT);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitState, setSubmitState] = useState("idle");

  // Reset draft + clear errors every time the modal opens. Local
  // effect only — mirrors ClientLogCommunicationModal.
  useEffect(() => {
    if (open) {
      setDraft(INITIAL_DRAFT);
      setFieldErrors({});
      setFormError(null);
      setSubmitState("idle");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const submitting = submitState === "submitting";

  const handleFieldChange = (field) => (event) => {
    const next = event.target.value;
    setDraft((prev) => ({ ...prev, [field]: next }));
    setFieldErrors((prev) => {
      if (!prev[field] && !prev._form) return prev;
      const out = { ...prev };
      delete out[field];
      delete out._form;
      return out;
    });
    setFormError(null);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (submitting) return;

    setSubmitState("submitting");
    setFieldErrors({});
    setFormError(null);

    const body = {
      title: draft.title.trim(),
      description: draft.description ?? "",
      priority: draft.priority,
      client_id: clientId,
    };
    const due = dueDateForApi(draft.due_date);
    if (due !== null) {
      body.due_date = due;
    }

    try {
      await createTask(body);
      setSubmitState("saved");
      onSaved();
    } catch (err) {
      const parsed = parseDrfFieldErrors(err, EDITABLE_KEYS);
      setFieldErrors(parsed);
      setFormError(parsed._form || messageFromError(err));
      setSubmitState("error");
    }
  };

  return (
    <div
      className="feature-client-detail__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-create-task-modal-title"
    >
      <div
        className="feature-client-detail__modal-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="feature-client-detail__modal-dialog">
        <header className="feature-client-detail__modal-head">
          <h2
            id="client-create-task-modal-title"
            className="feature-client-detail__modal-title"
          >
            <i className="fa-solid fa-list-check" aria-hidden="true" />
            <span>Нова задача</span>
          </h2>
          <button
            type="button"
            className="feature-client-detail__modal-close"
            onClick={onClose}
            aria-label="Закрити вікно створення задачі"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-client-detail__modal-body">
          <form className="ui-form" onSubmit={handleSubmit} noValidate>
            <div className="ui-form__rows">
              <Field
                id="client-create-task-title"
                label="Заголовок"
                required
                error={fieldErrors.title}
              >
                <TextInput
                  name="title"
                  value={draft.title}
                  onChange={handleFieldChange("title")}
                  maxLength={255}
                  disabled={submitting}
                  placeholder="Що потрібно зробити?"
                />
              </Field>

              <Field
                id="client-create-task-description"
                label="Опис"
                error={fieldErrors.description}
              >
                <Textarea
                  name="description"
                  value={draft.description}
                  onChange={handleFieldChange("description")}
                  rows={4}
                  placeholder="Деталі задачі…"
                  disabled={submitting}
                />
              </Field>

              <Field
                id="client-create-task-priority"
                label="Пріоритет"
                error={fieldErrors.priority}
              >
                <Select
                  name="priority"
                  value={draft.priority}
                  onChange={handleFieldChange("priority")}
                  options={PRIORITY_OPTIONS}
                  disabled={submitting}
                />
              </Field>

              <Field
                id="client-create-task-due"
                label="Дедлайн"
                error={fieldErrors.due_date}
              >
                <TextInput
                  type="datetime-local"
                  name="due_date"
                  value={draft.due_date}
                  onChange={handleFieldChange("due_date")}
                  disabled={submitting}
                />
              </Field>
            </div>

            <div className="ui-form__messages">
              {formError ? (
                <InlineError className="ui-form__message">
                  {formError}
                </InlineError>
              ) : null}
            </div>

            <div className="ui-form__actions">
              <button
                type="button"
                className="ui-form__button ui-form__button--ghost"
                onClick={onClose}
                disabled={submitting}
              >
                Скасувати
              </button>
              <button
                type="submit"
                className="ui-form__button ui-form__button--primary"
                disabled={submitting}
                data-loading={submitting ? "true" : undefined}
              >
                {submitting ? (
                  <Spinner size="sm" label="Створення" />
                ) : (
                  "Створити"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ClientCreateTaskModal;
